import { Fragment } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useQuery } from '@tanstack/react-query'
import { formatAmount, formatDate, formatPerson } from './formatting'
import { formatReceiverPartyForDisplay } from '../../../utils/phoneFormat'
import { getReversal, getVendorInitiatedReversal } from '../../../services/ReversalService'

// Shared details modal for a single reversal row on either tab. Fetches
// the show endpoint on open to pull fuller meta and the ledger entries;
// falls back to the row-shape data supplied via the `reversal` prop while
// the fetch is in flight or if it fails, so the modal is always useful.
// The `variant` prop switches header colour and content shape.
const ReversalDetailsModal = ({ show, onHide, variant, reversal, statusBadge }) => {
    const identifier = reversal
        ? (variant === 'vendor' ? reversal.uuid : reversal.id)
        : null

    const detailQuery = useQuery({
        queryKey: ['reversals', variant === 'vendor' ? 'vendor-initiated' : 'admin-initiated', 'show', identifier],
        queryFn:  () => variant === 'vendor'
            ? getVendorInitiatedReversal(identifier)
            : getReversal(identifier),
        enabled:  show && identifier != null,
        staleTime: 0,
    })

    if (!reversal) return null

    const detail = detailQuery.data?.data?.data ?? reversal

    const accent = variant === 'vendor'
        ? { rgba: 'rgba(40,167,69,0.15)',  text: 'text-success', label: 'Vendor initiated' }
        : { rgba: 'rgba(13,110,253,0.15)', text: 'text-primary', label: 'Admin initiated' }

    return (
        <Modal show={show} onHide={onHide} centered size="lg" scrollable>
            <Modal.Header closeButton style={{ borderBottom: `1px solid ${accent.rgba}` }}>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <i
                        className={variant === 'vendor'
                            ? 'bi bi-arrow-down-circle-fill'
                            : 'bi bi-arrow-left-circle-fill'}
                        aria-hidden="true"
                        style={{ fontSize: '1.4rem' }}
                    />
                    <span className={accent.text}>{accent.label} reversal</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {variant === 'vendor'
                    ? <VendorDetails detail={detail} />
                    : <AdminDetails detail={detail} statusBadge={statusBadge} />}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

const DetailRow = ({ label, children }) => (
    <div className="row mb-3">
        <div className="col-sm-5 text-uppercase text-muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', fontWeight: 600, paddingTop: '0.2rem' }}>
            {label}
        </div>
        <div className="col-sm-7" style={{ wordBreak: 'break-word' }}>
            {children}
        </div>
    </div>
)

const monoStyle = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.08em' }

// Server-side payout_type enum values and their human-friendly labels.
// Keys MUST match the backend enum's ->value literally: the payout type
// enum uses hyphenated slugs (`send-money`, `pochi-la-biashara`) so a
// non-hyphenated key here silently falls back to rendering the raw slug.
// Two extra pseudo-types are the ones the admin flow can also reverse
// against (partner top-ups and capital injections). Unknown types fall
// back to their raw slug so we never render an empty cell.
const TRANSACTION_TYPE_LABELS = {
    'send-money':        'Send Money',
    'paybill':           'Paybill',
    'till':              'Till',
    'pochi-la-biashara': 'Pochi la Biashara',
    'partner_top_up':    'Partner top-up',
    'capital_injection': 'Capital injection',
}

const formatTransactionType = (type) => {
    if (!type) return '-'
    return TRANSACTION_TYPE_LABELS[type] ?? type
}

// Highlighted pill styling that gives an Original Receipt ID (the payout
// this reversal points back at) a bit more visual weight than a plain
// monospace value, matching the treatment used on the mobile cards.
const originalReceiptPill = (variant) => {
    const bg     = variant === 'vendor' ? 'rgba(40,167,69,0.10)'  : 'rgba(13,110,253,0.10)'
    const border = variant === 'vendor' ? 'rgba(40,167,69,0.28)'  : 'rgba(13,110,253,0.28)'
    return {
        ...monoStyle,
        display:         'inline-block',
        padding:         '0.25rem 0.6rem',
        borderRadius:    '0.375rem',
        backgroundColor: bg,
        border:          `1px solid ${border}`,
        fontWeight:      700,
    }
}

const VendorDetails = ({ detail }) => {
    const recordedAt = detail.initiated_at ?? detail.created_at
    const feeShare   = detail.platform_fee_refund_fee_share
    const taxShare   = detail.platform_fee_refund_tax_share
    const platform   = detail.platform_fee_refund_amount
    const mpesaFee   = detail.mpesa_fee_refund_amount
    return (
        <Fragment>
            <DetailRow label="Refund ID">
                <span style={monoStyle}>{detail.inbound_mpesa_receipt_number}</span>
            </DetailRow>
            {detail.outbound_mpesa_receipt_number && (
                <DetailRow label="Original Receipt ID">
                    <span style={originalReceiptPill('vendor')}>{detail.outbound_mpesa_receipt_number}</span>
                </DetailRow>
            )}
            <DetailRow label="Refunded To">
                {formatPerson(detail.source_user)}
            </DetailRow>
            <DetailRow label="Transaction type">
                {formatTransactionType(detail.source_transaction_type)}
            </DetailRow>
            <DetailRow label="Inbound amount">
                <span className="fw-semibold">{formatAmount(detail.inbound_amount)}</span>
            </DetailRow>
            {Number(platform) > 0 && (
                <DetailRow label="Platform fee refund">
                    <span className="fw-semibold">{formatAmount(platform)}</span>
                    <div className="small text-muted">
                        Revenue reversal {formatAmount(feeShare)} · Tax reversal {formatAmount(taxShare)}
                    </div>
                </DetailRow>
            )}
            {Number(mpesaFee) > 0 && (
                <DetailRow label="M-Pesa fee refund">
                    <span className="fw-semibold">{formatAmount(mpesaFee)}</span>
                </DetailRow>
            )}
            {detail.comment && (
                <DetailRow label="Comment">
                    <span className="fst-italic">{detail.comment}</span>
                </DetailRow>
            )}
            <DetailRow label="Recorded by">{formatPerson(detail.initiated_by)}</DetailRow>
            <DetailRow label="Recorded">{recordedAt ? formatDate(recordedAt) : '-'}</DetailRow>
        </Fragment>
    )
}

const AdminDetails = ({ detail, statusBadge }) => (
    <Fragment>
        {detail.post_mpesa_transaction_id && (
            <DetailRow label="Refund ID">
                <span style={monoStyle}>{detail.post_mpesa_transaction_id}</span>
            </DetailRow>
        )}
        <DetailRow label="Original Receipt ID">
            <span style={originalReceiptPill('admin')}>{detail.mpesa_receipt_number}</span>
        </DetailRow>
        <DetailRow label="Amount">
            <span className="fw-semibold">{formatAmount(detail.amount)}</span>
        </DetailRow>
        {detail.receiver_party && (
            <DetailRow label="Original Recipient">
                {formatReceiverPartyForDisplay(detail.receiver_party)}
            </DetailRow>
        )}
        <DetailRow label="Transaction type">
            {formatTransactionType(detail.source_transaction_type)}
        </DetailRow>
        <DetailRow label="Status">{statusBadge}</DetailRow>
        {detail.post_mpesa_result_description && (
            <DetailRow label="Safaricom message">
                <span className="small">{detail.post_mpesa_result_description}</span>
            </DetailRow>
        )}
        {detail.comment && (
            <DetailRow label="Comment">
                <span className="fst-italic">{detail.comment}</span>
            </DetailRow>
        )}
        <DetailRow label="Initiated by">{formatPerson(detail.initiated_by)}</DetailRow>
        <DetailRow label="Date">{formatDate(detail.created_at)}</DetailRow>
    </Fragment>
)

export default ReversalDetailsModal
